// Pure formatting / icon helpers for the document list. No IO, no state —
// safe to import from pure leaf components and their stories.

import {
  File,
  FileAudio,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Image,
  type LucideIcon,
} from 'lucide-react'
import { isAudioFileType } from '@/utils/audio-playback'
import { isVideoFileType } from '@/utils/video-file'

/** Resolve a file-type string to its icon component + tailwind color class. */
export function getFileIconMeta(fileType: string | null): {
  Icon: LucideIcon
  colorClass: string
} {
  const type = fileType?.toLowerCase()
  if (type === 'pdf') return { Icon: FileText, colorClass: 'text-red-500' }
  if (type === 'xlsx' || type === 'xls' || type === 'csv')
    return { Icon: FileSpreadsheet, colorClass: 'text-emerald-600' }
  if (type === 'docx' || type === 'doc') return { Icon: FileText, colorClass: 'text-blue-500' }
  if (type === 'jpg' || type === 'jpeg' || type === 'png' || type === 'gif' || type === 'webp')
    return { Icon: Image, colorClass: 'text-purple-500' }
  if (isAudioFileType(type)) return { Icon: FileAudio, colorClass: 'text-amber-500' }
  if (isVideoFileType(type)) return { Icon: FileVideo, colorClass: 'text-cat-5' }
  return { Icon: File, colorClass: 'text-muted-foreground' }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}
